import React from "react";
import { CoolSwitch, Select, ArrowDown, ArrowUp } from "@coolremote/shared";
import {
  IModbusDeviceRow,
  UI_TYPE_BINARY,
  UI_TYPE_ENUMERATION,
  UI_TYPE_REVERSE_BINARY,
  UI_TYPE_VALUE,
} from "../../../Modbus/types";
import type { IUIControlPoint, IUITemplate, UILabelPosition, UILabelSize } from "../../../Modbus/UITemplateMapper";
import { getModbusRegisterKey } from "../../../Modbus/ModbusUnitControl";
import { engUnitLabel } from "../../../Modbus/RegisterTable";
import styles from "./ModbusUIRenderer.module.css";

// ─── Value helpers (mirrors ModbusUnitControl) ─────────────────────────────────

const FONT_SIZES: Record<UILabelSize, number> = { xs: 10, sm: 12, md: 15, lg: 19 };

const getRaw = (row: IModbusDeviceRow, opData: Record<string, any>): number | null => {
  const key = getModbusRegisterKey(row);
  const raw = key != null ? opData[key] : undefined;
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return isNaN(n) ? null : n;
};

const getDisplayText = (row: IModbusDeviceRow, value: number | null): string => {
  if (value === null) return "—";
  switch (row.uiType) {
    case UI_TYPE_BINARY: {
      const key = String(Math.round(value));
      return row.customEnum?.[key] ?? (value ? "On" : "Off");
    }
    case UI_TYPE_REVERSE_BINARY: {
      const displayVal = value === 0 ? 1 : 0;
      const key = String(displayVal);
      return row.customEnum?.[key] ?? (displayVal ? "On" : "Off");
    }
    case UI_TYPE_ENUMERATION: {
      const key = String(Math.round(value));
      return row.customEnum?.[key] ?? key;
    }
    default:
      return `${value}${row.units ? ` ${engUnitLabel(row.units)}` : ""}`;
  }
};

// ─── Label layout helper ──────────────────────────────────────────────────────

const LabeledControl: React.FC<{
  point: IUIControlPoint;
  label: React.ReactNode;
  control: React.ReactNode;
}> = ({ point, label, control }) => {
  const pos: UILabelPosition = point.labelPosition;
  if (pos === "top") {
    return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>{label}{control}</div>;
  }
  if (pos === "bottom") {
    return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>{control}{label}</div>;
  }
  if (pos === "right") {
    return <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{control}{label}</div>;
  }
  // default: left
  return <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{label}{control}</div>;
};

// ─── Per-point widget ─────────────────────────────────────────────────────────

interface IWidgetProps {
  point: IUIControlPoint;
  row: IModbusDeviceRow;
  operationalData: Record<string, any>;
  readOnly: boolean;
  onSetValue: (row: IModbusDeviceRow, rowIndex: number, value: number) => void;
}

const ControlPointWidget: React.FC<IWidgetProps> = ({ point, row, operationalData, readOnly, onSetValue }) => {
  const value = getRaw(row, operationalData);
  const displayText = getDisplayText(row, value);
  const isReadOnly = readOnly || row.readOnly;

  const fontSize = FONT_SIZES[point.labelSize];
  const color = point.textColor;

  const label = (
    <span className={styles.label} style={{ fontSize, color }}>{point.labelName}</span>
  );

  const control = (() => {
    switch (point.controlType) {
      case "onoff": {
        const isBinary = row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY;
        const isOn = value != null && Math.round(value) === (row.uiType === UI_TYPE_REVERSE_BINARY ? 0 : 1);
        return (
          <button
            className={`${styles.onOffBtn} ${isOn ? styles.onOffBtnOn : ""}`}
            disabled={!isBinary || isReadOnly}
            onClick={() => {
              const next = isOn
                ? (row.uiType === UI_TYPE_REVERSE_BINARY ? 1 : 0)
                : (row.uiType === UI_TYPE_REVERSE_BINARY ? 0 : 1);
              onSetValue(row, point.rowIndex, next);
            }}
          >
            ⏻
          </button>
        );
      }

      case "toggle": {
        const isBinary = row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY;
        const isOn = value != null && Math.round(value) === (row.uiType === UI_TYPE_REVERSE_BINARY ? 0 : 1);
        return (
          <CoolSwitch
            checked={isOn}
            disabled={!isBinary || isReadOnly}
            onChange={(e: any) => {
              if (!isBinary || isReadOnly) return;
              const next = e.target.checked
                ? (row.uiType === UI_TYPE_REVERSE_BINARY ? 0 : 1)
                : (row.uiType === UI_TYPE_REVERSE_BINARY ? 1 : 0);
              onSetValue(row, point.rowIndex, next);
            }}
          />
        );
      }

      case "dropdown": {
        const isEnum = row.uiType === UI_TYPE_ENUMERATION;
        const entries = isEnum ? Object.entries(row.customEnum || {}) : [];
        const selectedKey = value != null ? String(Math.round(value)) : (entries[0]?.[0] ?? "");
        const options = entries.map(([k, v]) => ({ value: k, label: v as string }));
        return (
          <Select
            value={selectedKey}
            options={options.length ? options : [{ value: "", label: displayText }]}
            disabled={!isEnum || isReadOnly || entries.length === 0}
            menuPortal
            classes={{ trigger: styles.selectTrigger }}
            onChange={(v: any) => {
              const num = Number(v);
              if (!isNaN(num)) onSetValue(row, point.rowIndex, num);
            }}
          />
        );
      }

      case "updown": {
        const isNumeric = row.uiType === UI_TYPE_VALUE;
        const current = value ?? row.minValue ?? 0;
        const min = row.minValue;
        const max = row.maxValue;
        // Honour the param's configured step (mirrors ModbusUnitControl's
        // stepper) — this was hardcoded to 1, so a saved step was ignored.
        const step = row.step ?? 1;
        // Fractional steps accumulate float error (0.1 + 0.2), so round the
        // same way ModbusUnitControl does before sending.
        const round = (v: number) => parseFloat(v.toFixed(10));
        const canDown = !isReadOnly && isNumeric && (min == null || current - step >= min);
        const canUp = !isReadOnly && isNumeric && (max == null || current + step <= max);
        return (
          <div className={styles.upDown}>
            <button className={styles.upDownBtn} disabled={!canDown} onClick={() => onSetValue(row, point.rowIndex, round(current - step))}>
              <ArrowDown color={color} />
            </button>
            <span className={styles.upDownVal}>{displayText}</span>
            <button className={styles.upDownBtn} disabled={!canUp} onClick={() => onSetValue(row, point.rowIndex, round(current + step))}>
              <ArrowUp color={color} />
            </button>
          </div>
        );
      }

      default:
        return (
          <span className={styles.valueText} style={{ fontSize, color }}>{displayText}</span>
        );
    }
  })();

  return (
    <div
      className={styles.widget}
      style={{
        left: point.x < 16 ? `calc(${point.x}% + 16px)` : `${point.x}%`,
        top: point.y < 16 ? `calc(${point.y}% + 16px)` : `${point.y}%`,
      }}
      >
      <LabeledControl point={point} label={label} control={control} />
    </div>
  );
};

// ─── Main renderer ────────────────────────────────────────────────────────────

interface IModbusUIRendererProps {
  uiTemplate: IUITemplate;
  rows: IModbusDeviceRow[];
  operationalData: Record<string, any>;
  readOnly?: boolean;
  onSetValue: (row: IModbusDeviceRow, rowIndex: number, value: number) => void;
}

const ModbusUIRenderer: React.FC<IModbusUIRendererProps> = ({
  uiTemplate: template,
  rows,
  operationalData,
  readOnly = false,
  onSetValue,
}) => {
  if (!template) return null;

  return (
    <div className={styles.canvas}>
      {template.backgroundImage && (
        <img src={template.backgroundImage} className={styles.bgImage} alt="" draggable={false} />
      )}
      {template.points.map((pt) => {
        const row = rows[pt.rowIndex];
        if (!row) return null;
        return (
          <ControlPointWidget
            key={pt.id}
            point={pt}
            row={row}
            operationalData={operationalData}
            readOnly={readOnly}
            onSetValue={onSetValue}
          />
        );
      })}
    </div>
  );
};

export default ModbusUIRenderer;
